import {ReactNode} from "react";
import {useConfirmDelete} from "@/hooks/useConfirmDelete";
import {getErrMsg} from "@/lib/errors";
import {notify} from "@/components/Notifications";
import ConfirmDialog from "@/components/ConfirmDialog";
import {Trash} from "lucide-react";

type Props = {
    onDelete: () => Promise<unknown>;
    itemLabel?: string;
    itemName?: string;
    title?: string;
    description?: ReactNode;
    confirmText?: string;
    tone?: "danger" | "primary";
    className?: string;
    children?: ReactNode;
    renderButton?: (opts: { onClick: () => void; disabled: boolean }) => ReactNode;
    onSuccess?: () => void;
    onError?: (e: unknown) => void;
};

export default function ConfirmDeleteButton({
                                                onDelete,
                                                itemLabel = "Item",
                                                itemName,
                                                title,
                                                description,
                                                confirmText = "Delete",
                                                tone = "danger",
                                                className = "text-red-600 hover:text-red-500",
                                                children,
                                                renderButton,
                                                onSuccess,
                                                onError,
                                            }: Props) {
    const {open, pending, openDialog, closeDialog, confirm} = useConfirmDelete({
        onDelete,
        onSuccess,
        onError: (e) => {
            onError?.(e);
            if (!onError) notify.error(getErrMsg(e, `Could not delete ${itemLabel.toLowerCase()}.`));
        },
    });

    const defaultTitle = title ?? `Delete ${itemLabel}?`;
    const defaultDescription =
        description ?? <>Are you sure you want to delete <strong>{itemName ?? itemLabel}</strong>? This action cannot be
            undone.</>;

    const dialogTone: "danger" | "default" = tone === "danger" ? "danger" : "default";

    return (
        <>
            {renderButton ? (
                renderButton({onClick: openDialog, disabled: pending})
            ) : (
                <button
                    type="button"
                    onClick={openDialog}
                    disabled={pending}
                    className={className}
                    aria-label={`Delete ${itemName ?? itemLabel}`}
                >
                    <Trash/>
                </button>
            )}

            <ConfirmDialog
                open={open}
                onClose={closeDialog}
                title={defaultTitle}
                description={defaultDescription}
                confirmText={confirmText}
                tone={dialogTone}
                onConfirm={confirm}
            />
        </>
    );
}
